import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, type EntriesQuery } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Alert, EmptyState, LoadingBlock, PaymentBadge, Spinner, StatusBadge } from '../components/ui';
import { ConfirmDialog } from '../components/Modal';
import { EntryFormModal } from './EntryFormModal';
import { ImportModal } from './ImportModal';
import { formatDateShort, formatSwimTimeShort, labelFor } from '../lib/format';
import {
  ENTRY_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  type AdminEvent,
  type Entry,
  type EntryStatus,
  type PaymentStatus,
} from '../lib/types';

const PAGE_SIZE = 50;

type SortKey = NonNullable<EntriesQuery['sort']>;

export function EntriesPage() {
  const [params, setParams] = useSearchParams();

  const query: EntriesQuery = useMemo(
    () => ({
      q: params.get('q') ?? undefined,
      status: params.get('status') ?? undefined,
      paymentStatus: params.get('paymentStatus') ?? undefined,
      categoryId: params.get('categoryId') ?? undefined,
      sort: (params.get('sort') as SortKey | null) ?? 'createdAt',
      direction: (params.get('direction') as 'asc' | 'desc' | null) ?? 'desc',
      limit: PAGE_SIZE,
      offset: Number(params.get('offset') ?? '0') || 0,
    }),
    [params],
  );

  const event = useAsync<AdminEvent>((signal) => api.admin.getEvent(signal), []);
  const list = useAsync(
    (signal) => api.admin.listEntries(query, signal),
    [
      query.q,
      query.status,
      query.paymentStatus,
      query.categoryId,
      query.sort,
      query.direction,
      query.offset,
    ],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Entry | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Entry | null>(null);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const updateParam = useCallback(
    (patch: Record<string, string | undefined>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === '') next.delete(key);
        else next.set(key, value);
      }
      // Any filter change resets paging.
      if (!('offset' in patch)) next.delete('offset');
      setParams(next, { replace: true });
      setSelected(new Set());
    },
    [params, setParams],
  );

  // Search box is debounced so typing does not fire a request per keystroke.
  const [searchDraft, setSearchDraft] = useState(query.q ?? '');
  const committedSearch = query.q ?? '';
  const updateParamRef = useRef(updateParam);
  updateParamRef.current = updateParam;

  useEffect(() => {
    if (searchDraft === committedSearch) return;
    const timer = window.setTimeout(() => updateParamRef.current({ q: searchDraft }), 300);
    return () => window.clearTimeout(timer);
  }, [searchDraft, committedSearch]);

  const entries = list.data?.entries ?? [];
  const pagination = list.data?.pagination;
  const categoryLabels = new Map((event.data?.categories ?? []).map((c) => [c.id, c.label]));

  const allOnPageSelected = entries.length > 0 && entries.every((e) => selected.has(e.entryId));

  function toggleAll() {
    setSelected(allOnPageSelected ? new Set() : new Set(entries.map((e) => e.entryId)));
  }

  function toggleOne(entryId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  }

  function sortBy(key: SortKey) {
    const sameKey = query.sort === key;
    updateParam({
      sort: key,
      direction: sameKey && query.direction === 'asc' ? 'desc' : 'asc',
    });
  }

  async function applyBulk(patch: { status?: EntryStatus; paymentStatus?: PaymentStatus }) {
    if (selected.size === 0) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await api.admin.bulkStatus([...selected], patch);
      setNotice(
        `Updated ${result.updated} ${result.updated === 1 ? 'entry' : 'entries'}.` +
          (result.failed.length > 0 ? ` ${result.failed.length} could not be updated.` : ''),
      );
      setSelected(new Set());
      list.reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Bulk update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.admin.deleteEntry(deleting.entryId);
      setNotice(`Deleted ${deleting.firstName} ${deleting.lastName} (${deleting.reference}).`);
      setDeleting(null);
      list.reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not delete the entry.');
    } finally {
      setBusy(false);
    }
  }

  async function exportCsv() {
    setActionError(null);
    try {
      await api.admin.exportEntries();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Export failed.');
    }
  }

  if (event.loading) return <LoadingBlock message="Loading entries..." />;
  if (event.error || !event.data) {
    return (
      <Alert variant="error" title="Could not load the event">
        <p>{event.error ?? 'Please try again.'}</p>
      </Alert>
    );
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1>Entries</h1>
          <p>
            {pagination
              ? `${pagination.total.toLocaleString('en-GB')} matching of ${pagination.totalUnfiltered.toLocaleString('en-GB')} total`
              : 'Loading...'}
          </p>
        </div>
        <div className="row">
          <button type="button" className="btn btn-secondary" onClick={() => void exportCsv()}>
            Export CSV
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setImporting(true)}>
            Import CSV
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing(null)}>
            Add entry
          </button>
        </div>
      </div>

      {notice && (
        <Alert variant="success">
          <p style={{ marginBottom: 0 }}>{notice}</p>
        </Alert>
      )}
      {actionError && (
        <Alert variant="error">
          <p style={{ marginBottom: 0 }}>{actionError}</p>
        </Alert>
      )}

      <div className="toolbar">
        <div className="field toolbar-grow">
          <label htmlFor="entry-search">Search</label>
          <input
            id="entry-search"
            type="search"
            placeholder="Name, email, reference, club or phone"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="filter-status">Status</label>
          <select
            id="filter-status"
            value={query.status ?? ''}
            onChange={(e) => updateParam({ status: e.target.value })}
          >
            <option value="">All statuses</option>
            {ENTRY_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="filter-payment">Payment</label>
          <select
            id="filter-payment"
            value={query.paymentStatus ?? ''}
            onChange={(e) => updateParam({ paymentStatus: e.target.value })}
          >
            <option value="">All payments</option>
            {PAYMENT_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="filter-category">Category</label>
          <select
            id="filter-category"
            value={query.categoryId ?? ''}
            onChange={(e) => updateParam({ categoryId: e.target.value })}
          >
            <option value="">All categories</option>
            {event.data.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {(query.q || query.status || query.paymentStatus || query.categoryId) && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setSearchDraft('');
              updateParam({ q: '', status: '', paymentStatus: '', categoryId: '', offset: '' });
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <div className="bulk-bar" role="region" aria-label="Bulk actions">
          <strong>
            {selected.size} selected
          </strong>
          <label className="checkbox-inline">
            <span className="visually-hidden">Set status for selected entries</span>
            <select
              defaultValue=""
              disabled={busy}
              onChange={(e) => {
                if (e.target.value) void applyBulk({ status: e.target.value as EntryStatus });
                e.target.value = '';
              }}
            >
              <option value="">Set status to...</option>
              {ENTRY_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="checkbox-inline">
            <span className="visually-hidden">Set payment status for selected entries</span>
            <select
              defaultValue=""
              disabled={busy}
              onChange={(e) => {
                if (e.target.value)
                  void applyBulk({ paymentStatus: e.target.value as PaymentStatus });
                e.target.value = '';
              }}
            >
              <option value="">Set payment to...</option>
              {PAYMENT_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {busy && <Spinner label="Applying" />}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {list.loading ? (
        <LoadingBlock message="Loading entries..." />
      ) : list.error ? (
        <Alert variant="error" title="Could not load entries">
          <p>{list.error}</p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={list.reload}>
            Retry
          </button>
        </Alert>
      ) : entries.length === 0 ? (
        <EmptyState title="No entries match">
          <p>Try clearing the filters, or add an entry manually.</p>
        </EmptyState>
      ) : (
        <>
          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">Event entries</caption>
              <thead>
                <tr>
                  <th scope="col" className="col-select">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAll}
                      aria-label="Select all entries on this page"
                    />
                  </th>
                  <SortableHeader
                    label="Reference"
                    sortKey="reference"
                    active={query.sort}
                    direction={query.direction}
                    onSort={sortBy}
                  />
                  <SortableHeader
                    label="Name"
                    sortKey="lastName"
                    active={query.sort}
                    direction={query.direction}
                    onSort={sortBy}
                  />
                  <th scope="col">Contact</th>
                  <th scope="col">Category</th>
                  <SortableHeader
                    label="Predicted"
                    sortKey="seedTime"
                    active={query.sort}
                    direction={query.direction}
                    onSort={sortBy}
                  />
                  <th scope="col">Age band</th>
                  <th scope="col">Status</th>
                  <th scope="col">Payment</th>
                  <SortableHeader
                    label="Entered"
                    sortKey="createdAt"
                    active={query.sort}
                    direction={query.direction}
                    onSort={sortBy}
                  />
                  <th scope="col" className="col-actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.entryId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(entry.entryId)}
                        onChange={() => toggleOne(entry.entryId)}
                        aria-label={`Select ${entry.firstName} ${entry.lastName}`}
                      />
                    </td>
                    <td className="mono nowrap">{entry.reference}</td>
                    <td>
                      <strong>
                        {entry.lastName}, {entry.firstName}
                      </strong>
                      {entry.club && <div className="faint">{entry.club}</div>}
                    </td>
                    <td>
                      <div className="faint">{entry.email}</div>
                      <div className="faint">{entry.phone}</div>
                    </td>
                    <td className="nowrap">{categoryLabels.get(entry.categoryId) ?? entry.categoryId}</td>
                    <td className="mono nowrap">{formatSwimTimeShort(entry.seedTimeMs)}</td>
                    <td className="nowrap muted">{entry.ageGroup}</td>
                    <td>
                      <StatusBadge status={entry.status} />
                    </td>
                    <td>
                      <PaymentBadge status={entry.paymentStatus} />
                      {entry.paymentReference && (
                        <div className="faint mono">{entry.paymentReference}</div>
                      )}
                    </td>
                    <td className="nowrap muted">{formatDateShort(entry.createdAt)}</td>
                    <td className="col-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditing(entry)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setDeleting(entry)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total > PAGE_SIZE && (
            <nav className="spread" aria-label="Entry pages">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={pagination.offset === 0}
                onClick={() =>
                  updateParam({ offset: String(Math.max(0, pagination.offset - PAGE_SIZE)) })
                }
              >
                Previous
              </button>
              <span className="faint">
                Showing {pagination.offset + 1}-
                {Math.min(pagination.offset + PAGE_SIZE, pagination.total)} of {pagination.total}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={pagination.offset + PAGE_SIZE >= pagination.total}
                onClick={() => updateParam({ offset: String(pagination.offset + PAGE_SIZE) })}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}

      {importing && (
        <ImportModal
          event={event.data}
          onClose={() => setImporting(false)}
          onImported={(summary) => {
            setImporting(false);
            setNotice(summary);
            list.reload();
          }}
        />
      )}

      {editing !== undefined && (
        <EntryFormModal
          event={event.data}
          entry={editing}
          onClose={() => setEditing(undefined)}
          onSaved={(saved, heatsStale) => {
            setEditing(undefined);
            setNotice(
              `Saved ${saved.firstName} ${saved.lastName} (${saved.reference}).` +
                (heatsStale
                  ? ' Seeding details changed, so the start lists should be regenerated.'
                  : ''),
            );
            list.reload();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete this entry?"
          danger
          busy={busy}
          confirmLabel="Delete entry"
          message={
            <>
              <p>
                <strong>
                  {deleting.firstName} {deleting.lastName}
                </strong>{' '}
                ({deleting.reference}) will be permanently removed, including their contact and
                medical details.
              </p>
              <p style={{ marginBottom: 0 }}>
                This cannot be undone. To keep the record but take them out of the event, set the
                status to <em>{labelFor('withdrawn')}</em> instead.
              </p>
            </>
          }
          onCancel={() => setDeleting(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  active,
  direction,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey | undefined;
  direction: 'asc' | 'desc' | undefined;
  onSort: (key: SortKey) => void;
}) {
  const isActive = active === sortKey;
  return (
    <th scope="col" aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="table-sort" onClick={() => onSort(sortKey)}>
        {label}
        <span aria-hidden="true">{isActive ? (direction === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}
